/**
 * Schema.org JSON-LD builders and injection.
 * Kept separate from the runtime OG/Twitter head mutation in seo.ts.
 */
import { SITE_URL, SITE_NAME, LOGO_URL, type SeoData } from '@/lib/seo';

const SCRIPT_ATTR = 'data-readdy-schema';

function clearSchemas(): void {
  document
    .querySelectorAll(`script[${SCRIPT_ATTR}]`)
    .forEach((el) => el.remove());
}

export function injectSchemas(objects: Record<string, unknown>[]): void {
  clearSchemas();
  objects.forEach((obj) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SCRIPT_ATTR, 'true');
    script.textContent = JSON.stringify(obj);
    document.head.appendChild(script);
  });
}

function organizationSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  };
}

function websiteSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

function articleSchema(seo: SeoData): Record<string, unknown> {
  const url = seo.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${seo.path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: seo.title,
    description: seo.description,
    image: seo.ogImage || LOGO_URL,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
    author: { '@type': 'Organization', name: 'AllChinaBuy Research Team' },
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: LOGO_URL } },
  };
}

function faqSchema(faqs: { q: string; a: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

function breadcrumbSchema(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

const HOME_FAQS: { q: string; a: string }[] = [
  {
    q: 'What is AllChinaBuy Spreadsheet?',
    a: 'AllChinaBuy Spreadsheet is a curated collection of product links, prices, and seller information organized in an easy-to-use format. It helps shoppers find the best deals on shoes, clothing, accessories, and more from Chinese shopping platforms.',
  },
  {
    q: 'Is AllChinaBuy safe to use?',
    a: 'AllChinaBuy implements standard security measures including encrypted payments, warehouse quality checks, and buyer protection policies. We recommend researching sellers and using the QC service before shipping items internationally.',
  },
  {
    q: 'How much does AllChinaBuy shipping cost?',
    a: 'Shipping costs vary by destination country, parcel weight, and shipping method. AllChinaBuy offers multiple shipping lines including EMS, DHL, FedEx, and budget options.',
  },
  {
    q: 'Does AllChinaBuy offer coupons or discount codes?',
    a: 'Yes, AllChinaBuy regularly provides coupons and promotional discount codes for new and existing customers. Check our coupon guide for current active codes and money-saving strategies.',
  },
  {
    q: 'How does AllChinaBuy compare to other shopping agents?',
    a: 'AllChinaBuy offers competitive shipping rates, a user-friendly interface, and comprehensive spreadsheet resources. Read our detailed comparison guides for more information.',
  },
  {
    q: 'What countries does AllChinaBuy ship to?',
    a: 'AllChinaBuy ships to most countries worldwide including the USA, UK, Canada, Germany, France, Denmark, Australia, and across Europe.',
  },
  {
    q: 'Can I get a refund if my order is wrong?',
    a: 'AllChinaBuy provides refund and return options depending on the seller policy and order stage. Items that have not been shipped from the warehouse can typically be returned.',
  },
  {
    q: 'How do I use the AllChinaBuy spreadsheet?',
    a: 'Using the AllChinaBuy spreadsheet is simple: browse categories, click product links to view items, add desired products to your cart, and proceed with the purchasing process.',
  },
];

const GUIDE_FAQS: { q: string; a: string }[] = [
  {
    q: 'How often is the AllChinaBuy spreadsheet updated?',
    a: 'AllChinaBuy spreadsheets are typically updated weekly or bi-weekly to reflect current prices, new product releases, and availability changes.',
  },
  {
    q: 'Can I request specific products to be added?',
    a: 'While the spreadsheets are curated by the AllChinaBuy team, you can often find specific products by using the search function or reaching out to customer support with product links.',
  },
  {
    q: 'What if a product link is broken?',
    a: 'Product links can occasionally break due to marketplace changes. Report it to the AllChinaBuy team or search for the product name directly on the marketplace.',
  },
  {
    q: 'Is there a mobile app for AllChinaBuy?',
    a: 'AllChinaBuy primarily operates through its web interface, which is mobile-responsive. You can access all features through your mobile browser.',
  },
  {
    q: 'How do I track my order?',
    a: 'Once your order ships, AllChinaBuy provides tracking numbers for most shipping methods. Track your package through the dashboard or the carrier website.',
  },
];

/**
 * Resolve the JSON-LD schema objects appropriate for the given route.
 */
export function resolveSchemas(path: string, seo: SeoData): Record<string, unknown>[] {
  switch (path) {
    case '/':
      return [
        organizationSchema(),
        websiteSchema(),
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${SITE_URL}/#webpage`,
          name: seo.title,
          description: seo.description,
          url: `${SITE_URL}/`,
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#organization` },
          primaryImageOfPage: { '@type': 'ImageObject', url: seo.ogImage || LOGO_URL },
        },
        faqSchema(HOME_FAQS),
      ];
    case '/allchinabuy-spreadsheet-complete-guide':
      return [
        organizationSchema(),
        articleSchema(seo),
        faqSchema(GUIDE_FAQS),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'AllChinaBuy Spreadsheet Complete Guide', path: seo.path },
        ]),
      ];
    case '/allchinabuy-review':
    case '/allchinabuy-shipping-cost':
    case '/allchinabuy-coupon':
    case '/allchinabuy-vs-superbuy':
    case '/allchinabuy-legit-safety':
      return [
        organizationSchema(),
        articleSchema(seo),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: seo.title, path: seo.path },
        ]),
      ];
    case '/about':
      return [
        organizationSchema(),
        {
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: seo.title,
          description: seo.description,
          url: `${SITE_URL}${seo.path}`,
          about: { '@id': `${SITE_URL}/#organization` },
        },
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'About', path: seo.path },
        ]),
      ];
    case '/contact':
      return [
        organizationSchema(),
        {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: seo.title,
          description: seo.description,
          url: `${SITE_URL}${seo.path}`,
        },
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Contact', path: seo.path },
        ]),
      ];
    default:
      return [];
  }
}